import ButtonFactory from "@components/Button/ButtonFactory";
import { ButtonType } from "@components/Button/utils/types";
import SearchTrigger from "@components/Header/SearchTrigger/SearchTrigger";
import IndividualSideMenuSection from "@components/Header/SideMenu/IndividualSideMenuSection";
import type { NavigationA } from "@components/Header/types";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import Edition from "@features/banner/Edition/Edition";
import { useGetHamburgerList } from "@hooks/useGetHamburgerList";
import { OKTAUserType } from "@store/useOKTAUserStore";
import {
  cn,
  mySPHOpenLogin,
  mySPHOpenLogout,
  mySPHOpenSignUp,
} from "@util/helpers";
import cx from "classnames";
import Cookies from "js-cookie";
import { useFeatureFlagEnabled } from "posthog-js/react";

interface SideMenuProps {
  data: NavigationA;
  isSideMenuOpen: boolean;
  handleOnClose: () => void;
  /** optional indicator to display edition switcher */
  isDisplayEditionSwitcher?: boolean;
}

export default function SideMenu({
  data,
  handleOnClose,
  isDisplayEditionSwitcher,
}: SideMenuProps): React.ReactElement {
  const isOverwritten = useFeatureFlagEnabled(
    "feature_overwrite_condition_edition"
  );

  const isGeoSg = Cookies.get("sph_user_country") === "SG";

  return (
    <SideMenuWrapper>
      <>
        <div className="flex justify-end px-8 pt-10 lg:hidden">
          <ButtonFactory
            buttonType={ButtonType.Secondary}
            onClick={handleOnClose}
            className="border-0"
            icon={
              <IconFactory
                type={IconTypeEnum.close}
                size={24}
                data-testid="side-menu-close-icon"
              />
            }
          />
        </div>

        <div
          className={cn("mx-8 lg:mx-5", {
            "border-b border-gray-250 pb-4 pt-4 lg:pb-3":
              isDisplayEditionSwitcher && (!isGeoSg || isOverwritten),
          })}
        >
          {isDisplayEditionSwitcher ? (
            <Edition className="pt-4" isGeoSg={isGeoSg} showLabel />
          ) : null}

          <SideMenuHeader />
        </div>

        <SideMenuNavItems headerData={data} />
      </>
    </SideMenuWrapper>
  );
}
interface SideMenuWrapperProps {
  children: React.ReactElement;
}
function SideMenuWrapper({
  children = <></>,
}: SideMenuWrapperProps): React.ReactElement | null {
  return (
    <div
      data-testid="side-menu-wrapper"
      className="min-h-screen bg-white pb-12 lg:pb-0"
    >
      {children}
    </div>
  );
}

function SideMenuHeader() {
  return (
    <div className="mt-4 flex items-center justify-center gap-4 border-gray-175 bg-gray-150 py-2 lg:hidden">
      <SearchTrigger className="rounded-[2px] text-gray-515" />
    </div>
  );
}

interface SideMenuNavItemsProps {
  headerData?: NavigationA;
}

function SideMenuNavItems({ headerData }: SideMenuNavItemsProps) {
  const hamburgerList = useGetHamburgerList(headerData || []);
  return (
    <nav className="flex flex-col bg-white">
      {hamburgerList?.map((section, index) => {
        return (
          <IndividualSideMenuSection
            key={`side-menu-${index}`}
            menuItems={section}
          />
        );
      })}
    </nav>
  );
}
interface RegisterAccountBlockProps {
  OKTAUserInfo: OKTAUserType;
}

export function RegisterAccountBlock({
  OKTAUserInfo,
}: RegisterAccountBlockProps) {
  const displayName = OKTAUserInfo?.display_name;
  const email = OKTAUserInfo?.loginid;

  const AccountInfoDisplay = () => {
    return (
      <div data-testid="AccountInfoDisplay">
        <div className="mb-3 font-public-sans text-base font-bold text-gray-900">
          Welcome, {displayName}
        </div>
        <div className="mb-3 border-b border-gray-450 pb-3 font-public-sans text-base font-medium text-gray-900">
          {email}
        </div>
        <div
          className={cx(
            "font-poppins font-semibold text-gray-850",
            "lg:hover:cursor-pointer"
          )}
          onClick={mySPHOpenLogout}
        >
          LOG OUT
        </div>
      </div>
    );
  };

  const RegisterAccountDisplay = () => {
    return (
      <div data-testid="RegisterAccountDisplay">
        <div className="mb-6 font-poppins text-base text-gray-650">
          Create a free account with Business Times for seamless access across
          SPH Media products.
        </div>
        <div className="flex items-center font-poppins font-semibold text-black">
          <div
            className={cx(
              "rounded-full border border-gray-525 bg-gray-100 px-6 py-1.5 text-base duration-300",
              "lg:hover:cursor-pointer lg:hover:bg-gray-300"
            )}
            onClick={mySPHOpenSignUp}
          >
            REGISTER
          </div>
          <div
            className={cx("ml-1 border px-6 py-1.5", "lg:hover:cursor-pointer")}
            onClick={mySPHOpenLogin}
          >
            LOGIN
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-250 px-6 py-4">
      {displayName ? <AccountInfoDisplay /> : <RegisterAccountDisplay />}
    </div>
  );
}
